import { normalizeCategoryName, stringToColor } from "./utils";

export const CATEGORY_CODE_MAX_DEPTH = 4;
const ROOT_STEP = 100;
const ROOT_PARENT_KEY = "__category_tree_root__";

function padSegment(value) {
  return String(value).padStart(3, "0");
}

export function parseCategoryCode(code) {
  const value = String(code || "").trim();
  if (!value) return [];

  const segments = value.split(".");
  if (
    segments.length > CATEGORY_CODE_MAX_DEPTH ||
    segments.some((segment) => !/^\d{3}$/.test(segment))
  ) {
    return [];
  }

  return segments;
}

export function normalizeCategoryCode(code) {
  const segments = parseCategoryCode(code);
  return segments.length ? segments.join(".") : "";
}

export function getCategoryCodeSegment(category) {
  const segments = parseCategoryCode(category?.codigo);
  return segments[segments.length - 1] || "---";
}

function codeSegmentsToNumbers(code) {
  return parseCategoryCode(code).map((segment) => parseInt(segment, 10));
}

export function compareCategoryCodes(a, b) {
  const aSegments = codeSegmentsToNumbers(a?.codigo);
  const bSegments = codeSegmentsToNumbers(b?.codigo);
  const maxLength = Math.max(aSegments.length, bSegments.length);

  for (let index = 0; index < maxLength; index += 1) {
    const aValue = aSegments[index] ?? -1;
    const bValue = bSegments[index] ?? -1;
    if (aValue !== bValue) return aValue - bValue;
  }

  return normalizeCategoryName(a?.name).localeCompare(normalizeCategoryName(b?.name), "pt-BR");
}

function sortStableCategories(items) {
  return [...items].sort((a, b) => {
    const codeDiff = compareCategoryCodes(a, b);
    if (codeDiff !== 0) return codeDiff;

    const dateDiff = String(a.created_at || "").localeCompare(String(b.created_at || ""));
    if (dateDiff !== 0) return dateDiff;

    return normalizeCategoryName(a.name).localeCompare(normalizeCategoryName(b.name), "pt-BR");
  });
}

function buildTypeScopedIds(categories, type) {
  return new Set(categories.filter((category) => category.type === type).map((category) => category.id));
}

function getEffectiveParentId(category, validIds) {
  return category?.parent_id && validIds.has(category.parent_id) ? category.parent_id : null;
}

function getCategoryType(categories, categoryId) {
  return categories.find((category) => category.id === categoryId)?.type;
}

export function getCategoryDepth(categories, categoryId) {
  const category = categories.find((item) => item.id === categoryId);
  if (!category) return 0;

  const validIds = buildTypeScopedIds(categories, category.type);
  const byId = new Map(categories.map((item) => [item.id, item]));
  const visited = new Set([categoryId]);
  let depth = 1;
  let parentId = getEffectiveParentId(category, validIds);

  while (parentId) {
    if (visited.has(parentId)) break;
    visited.add(parentId);
    const parent = byId.get(parentId);
    if (!parent || parent.type !== category.type) break;
    depth += 1;
    parentId = getEffectiveParentId(parent, validIds);
  }

  return depth;
}

export function getDescendantIds(categories, categoryId) {
  const categoryType = getCategoryType(categories, categoryId);
  if (!categoryType) return [];

  const validIds = buildTypeScopedIds(categories, categoryType);
  const childrenByParent = new Map();
  categories
    .filter((category) => category.type === categoryType)
    .forEach((category) => {
      const parentId = getEffectiveParentId(category, validIds);
      if (!parentId) return;
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(category);
    });

  const descendants = [];
  const visit = (parentId, visited = new Set()) => {
    if (visited.has(parentId)) return;
    visited.add(parentId);
    (childrenByParent.get(parentId) || []).forEach((child) => {
      descendants.push(child.id);
      visit(child.id, visited);
    });
  };

  visit(categoryId);
  return descendants;
}

function getSubtreeDepth(categories, categoryId) {
  const categoryType = getCategoryType(categories, categoryId);
  if (!categoryType) return 1;

  const validIds = buildTypeScopedIds(categories, categoryType);
  const childrenByParent = new Map();
  categories
    .filter((category) => category.type === categoryType)
    .forEach((category) => {
      const parentId = getEffectiveParentId(category, validIds);
      if (!parentId) return;
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(category);
    });

  const visit = (id, visited = new Set()) => {
    if (visited.has(id)) return 1;
    visited.add(id);
    const childDepths = (childrenByParent.get(id) || []).map((child) => visit(child.id, new Set(visited)));
    return 1 + (childDepths.length ? Math.max(...childDepths) : 0);
  };

  return visit(categoryId);
}

export function validateCategoryParent(categories, categoryId, parentId, type) {
  if (!parentId) return { valid: true };

  const parent = categories.find((category) => category.id === parentId);
  if (!parent || parent.type !== type) {
    return { valid: false, message: "Categoria pai inválida." };
  }

  if (categoryId && parentId === categoryId) {
    return { valid: false, message: "Uma categoria não pode ser pai dela mesma." };
  }

  if (categoryId && getDescendantIds(categories, categoryId).includes(parentId)) {
    return { valid: false, message: "Não é possível mover uma categoria para dentro de uma descendente." };
  }

  const parentDepth = getCategoryDepth(categories, parentId);
  const subtreeDepth = categoryId ? getSubtreeDepth(categories, categoryId) : 1;
  if (parentDepth + subtreeDepth > CATEGORY_CODE_MAX_DEPTH) {
    return { valid: false, message: "A hierarquia permite no máximo 4 níveis." };
  }

  return { valid: true };
}

export function generateNextCategoryCode(categories, type, parentId = null, excludeIds = []) {
  const excluded = new Set(excludeIds.filter(Boolean));
  const scoped = categories.filter((category) => category.type === type && !excluded.has(category.id));
  const validIds = buildTypeScopedIds(scoped, type);
  const parent = parentId ? scoped.find((category) => category.id === parentId) : null;

  if (!parent) {
    const usedRoots = scoped
      .filter((category) => !getEffectiveParentId(category, validIds))
      .map((category) => parseCategoryCode(category.codigo)[0])
      .filter(Boolean)
      .map((segment) => parseInt(segment, 10));

    for (let next = ROOT_STEP; next <= 999; next += ROOT_STEP) {
      if (!usedRoots.includes(next)) return padSegment(next);
    }

    const next = Math.max(0, ...usedRoots) + 1;
    return padSegment(Math.min(next, 999));
  }

  const parentSegments = parseCategoryCode(parent.codigo);
  if (!parentSegments.length || parentSegments.length >= CATEGORY_CODE_MAX_DEPTH) {
    return "";
  }

  const usedChildren = scoped
    .filter((category) => getEffectiveParentId(category, validIds) === parent.id)
    .map((category) => parseCategoryCode(category.codigo)[parentSegments.length])
    .filter(Boolean)
    .map((segment) => parseInt(segment, 10));

  const next = Math.max(0, ...usedChildren) + 1;
  if (next > 999) return "";

  return [...parentSegments, padSegment(next)].join(".");
}

export function normalizeCategoriesWithCodes(categories = []) {
  const normalized = categories.map((category) => ({
    ...category,
    name: normalizeCategoryName(category.name),
    parent_id: category.parent_id || null,
    codigo: normalizeCategoryCode(category.codigo),
    color: category.color || stringToColor(normalizeCategoryName(category.name) || "DEFAULT"),
  }));

  const byType = new Map();
  normalized.forEach((category) => {
    if (!byType.has(category.type)) byType.set(category.type, []);
    byType.get(category.type).push(category);
  });

  byType.forEach((items, type) => {
    const validIds = buildTypeScopedIds(normalized, type);
    const childrenByParent = new Map();

    items.forEach((category) => {
      const parentId = getEffectiveParentId(category, validIds);
      const key = parentId || ROOT_PARENT_KEY;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key).push(category);
    });

    const assignChildren = (parentId, visited = new Set()) => {
      if (visited.has(parentId)) return;
      visited.add(parentId);
      const children = sortStableCategories(childrenByParent.get(parentId) || []);
      children.forEach((child) => {
        if (!child.codigo) {
          child.codigo = generateNextCategoryCode(normalized, type, parentId === ROOT_PARENT_KEY ? null : parentId);
        }
        assignChildren(child.id, new Set(visited));
      });
    };

    assignChildren(ROOT_PARENT_KEY);
  });

  return normalized;
}

export function reassignCategorySubtreeCodes(categories, categoryId, nextParentId = null) {
  const normalized = normalizeCategoriesWithCodes(categories);
  const moved = normalized.find((category) => category.id === categoryId);
  if (!moved) return new Map();

  const descendants = getDescendantIds(normalized, categoryId);
  const excluded = [categoryId, ...descendants];
  const movedCode = generateNextCategoryCode(normalized, moved.type, nextParentId, excluded);
  if (!movedCode) return new Map();

  const updates = new Map([[categoryId, movedCode]]);
  const childrenByParent = new Map();
  normalized
    .filter((category) => category.type === moved.type)
    .forEach((category) => {
      if (!category.parent_id) return;
      if (!childrenByParent.has(category.parent_id)) childrenByParent.set(category.parent_id, []);
      childrenByParent.get(category.parent_id).push(category);
    });

  const assignDescendants = (parentId, parentCode, visited = new Set()) => {
    if (visited.has(parentId)) return;
    visited.add(parentId);
    sortStableCategories(childrenByParent.get(parentId) || []).forEach((child, index) => {
      const childCode = `${parentCode}.${padSegment(index + 1)}`;
      updates.set(child.id, childCode);
      assignDescendants(child.id, childCode, new Set(visited));
    });
  };

  assignDescendants(categoryId, movedCode);
  return updates;
}

export function flattenCategoryTree(categories = [], options = {}) {
  const {
    expandedIds = null,
    includeCollapsedChildren = true,
    sortSiblings = sortStableCategories,
  } = options;
  const normalized = normalizeCategoriesWithCodes(categories);
  const rows = [];

  const byType = new Map();
  normalized.forEach((category) => {
    if (!byType.has(category.type)) byType.set(category.type, []);
    byType.get(category.type).push(category);
  });

  byType.forEach((items, type) => {
    const validIds = buildTypeScopedIds(normalized, type);
    const childrenByParent = new Map();

    items.forEach((category) => {
      const parentId = getEffectiveParentId(category, validIds);
      const key = parentId || ROOT_PARENT_KEY;
      if (!childrenByParent.has(key)) childrenByParent.set(key, []);
      childrenByParent.get(key).push(category);
    });

    const visit = (parentId, depth, ancestors = [], visited = new Set()) => {
      if (visited.has(parentId)) return;
      visited.add(parentId);

      sortSiblings(childrenByParent.get(parentId) || []).forEach((category) => {
        const children = childrenByParent.get(category.id) || [];
        const isExpanded = expandedIds ? expandedIds.has(category.id) : true;
        rows.push({
          ...category,
          _depth: depth,
          _childrenCount: children.length,
          _isExpanded: isExpanded,
          _parentName: ancestors[ancestors.length - 1]?.name,
          _pathNames: [...ancestors.map((item) => item.name), category.name],
        });

        if (includeCollapsedChildren || isExpanded) {
          visit(category.id, depth + 1, [...ancestors, category], new Set(visited));
        }
      });
    };

    visit(ROOT_PARENT_KEY, 0);
  });

  return rows;
}
